import { Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonMessagesService } from '../../../../services/common-messages.service';
import { DefaultDeviceCatalog } from '../../../../model/default-device-catalog';
import { DefaultDevicesFormInfoComponent } from './default-devices-form-info/default-devices-form-info.component';
import { HwCatalogService } from '../../../../services/hw-catalog.service';
import { DefaultDeviceCatalogPersistRequest } from '../../../../model/requests/default-device-catalog-persist-request';
import { DeviceMeasurement } from '../../../../model/device-measurement';
import { DeviceProperty } from '../../../../model/device-property';
import { IntegrationModule } from '../../../../model/integration-module';

@Component({
  selector: 'app-default-devices-form',
  templateUrl: './default-devices-form.component.html',
  styleUrls: ['./default-devices-form.component.scss']
})
export class DefaultDevicesFormComponent implements OnInit {

  @Input() active: boolean;
  @Output() activeChange = new EventEmitter();

  items: any[];
  currentStep: number;
  stepChanged: boolean;

  deviceMeasurements: DeviceMeasurement[];
  deviceExpressions: DeviceMeasurement[];
  deviceProperties: DeviceProperty[];
  integrationModules: IntegrationModule[];

  defaultDeviceCatalog: DefaultDeviceCatalog;

  persistingChanges: boolean;
  formLoaded: boolean;
  formSuccess: boolean;

  mode: 'create' | 'edit';

  @ViewChild(DefaultDevicesFormInfoComponent)
  defaultDevicesCatalogFormInfo: DefaultDevicesFormInfoComponent;

  constructor(
    private hwCatalogService: HwCatalogService,
    private route: ActivatedRoute,
    private router: Router,
    private commonMessagesService: CommonMessagesService
  ) {
    this.defaultDeviceCatalog = new DefaultDeviceCatalog();
    this.deviceMeasurements = [];
    this.deviceProperties = [];
    this.deviceExpressions = [];
    this.integrationModules = [];
    this.formSuccess = false;
    this.stepChanged = false;
  }

  ngOnInit(): void {
    this.currentStep = 0;
    this.items = [
      { index: 0, icon: 'pi pi-list', label: 'model.defaultDevice', description: 'forms.descriptions.defaultDevice' },
      { index: 1, icon: 'pi pi-wifi', label: 'model.sensors', description: 'forms.descriptions.defaultDeviceSensors' },
      {
        index: 2,
        icon: 'pi pi-bars',
        label: 'model.expressions',
        description: 'forms.descriptions.defaultDeviceExpressions'
      },
      {
        index: 3,
        icon: 'pi pi-bars',
        label: 'model.properties',
        description: 'forms.descriptions.defaultDeviceProperties'
      },
      {
        index: 4,
        icon: 'pi pi-table',
        label: 'model.integrationModules',
        description: 'forms.descriptions.defaultDeviceIntegrationModules'
      },
      { index: 5, icon: 'pi pi-check', label: 'forms.confirmation' }
    ];

    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if( firstPath === 'new' ) {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  loadCreationForm(): void {
    this.defaultDeviceCatalog = new DefaultDeviceCatalog();
    this.formLoaded = true;
  }

  loadEditionForm(uuid): void {
    this.hwCatalogService.getDefaultDeviceCatalog(uuid).toPromise().then(response => {
      this.defaultDeviceCatalog = response.defaultDeviceCatalog;
      if( this.defaultDeviceCatalog.content?.deviceMeasurements ) {
        this.deviceMeasurements = this.defaultDeviceCatalog.content.deviceMeasurements.filter(measurement => !measurement.expression);
        this.deviceExpressions = this.defaultDeviceCatalog.content.deviceMeasurements.filter(measurement => measurement.expression);
      } else {
        this.deviceMeasurements = [];
        this.deviceExpressions = [];
      }
      this.deviceProperties = this.defaultDeviceCatalog.content?.deviceProperties ? this.defaultDeviceCatalog.content.deviceProperties : [];
      this.integrationModules = !!this.defaultDeviceCatalog.content?.integrationModules
        ? this.defaultDeviceCatalog.content.integrationModules.map(integrationModule => ({
          ...integrationModule,
          technicalDetails: !integrationModule.technicalDetails ? integrationModule.technicalDetails : JSON.parse(integrationModule.technicalDetails)
        }))
        : [];
      this.formLoaded = true;
    }, error => {
      if( error.status === 404 ) {
        this.router.navigate(['/not-found']);
      }
    });
  }

  persistDefaultDeviceCatalogData(): void {
    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if( this.mode === 'create' ) {
      promise = this.hwCatalogService.createDefaultDeviceCatalog(persistData).toPromise();
    } else {
      promise = this.hwCatalogService.editDefaultDeviceCatalog(persistData).toPromise();
    }

    promise.then(response => {
      if( this.active ) {
        this.active = false;
        this.activeChange.emit(this.active);
      }
      this.formSuccess = true;
      this.router.navigate(([`/system/hw-catalog/${ this.mode === 'create' ? response.uuid : persistData.uuid }/details`]));
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name + ' v' + persistData.version);
      this.persistingChanges = false;
    });
  }

  onNextStep(): void {
    this.stepChanged = true;
    if( this.currentStep < this.items.length - 1 ) {
      this.currentStep++;
    }
  }

  onPreviousStep(): void {
    this.stepChanged = true;
    if( this.currentStep > 0 ) {
      this.currentStep--;
    }
  }

  private buildPersistData(): DefaultDeviceCatalogPersistRequest {
    return {
      name: this.defaultDeviceCatalog.name,
      description: this.defaultDeviceCatalog.description,
      version: this.defaultDeviceCatalog.version,
      active: this.defaultDeviceCatalog.active,
      defaultDeviceCategory: this.defaultDeviceCatalog.defaultDeviceCategory,
      defaultAssetRepresentation: this.defaultDeviceCatalog.defaultAssetRepresentation,
      deviceMeasurements: this.deviceMeasurements.concat(this.deviceExpressions),
      deviceProperties: this.deviceProperties,
      integrationModules: this.integrationModules.map(integrationModule => ({
        ...integrationModule,
        technicalDetails: !integrationModule.technicalDetails ? integrationModule.technicalDetails : JSON.stringify(integrationModule.technicalDetails)
      })),
      uuid: this.mode === 'edit' ? this.defaultDeviceCatalog.uuid : null
    };
  }

  onCancel(route: string): Promise<boolean> {
    if( route == null ) {
      return this.router.navigate([`/system/hw-catalog`]);
    }
    return this.router.navigate([`${ route }`]);
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    if( this.formSuccess ) {
      return true;
    }
    return !this.stepChanged && (this.currentStep === 0 && this.defaultDevicesCatalogFormInfo.defaultDeviceCatalogForm.untouched);
  }

}
